/**
 * Enhanced Collection Page JavaScript
 * Handles tab filtering, hover effects, and URL management
 */

class CollectionEnhanced {
	constructor() {
		this.section = document.querySelector(".collection-enhanced");
		if (!this.section) return;

		this.paginationType =
			this.section.getAttribute("data-pagination-type") || "";

		this.sectionId = this.section.dataset.sectionId;
		this.collectionHandle = this.section.dataset.collectionHandle;

		// Get settings from JSON
		this.settings = this.getSettings();
		if (!this.settings) return;

		// Initialize caching system
		this.cache = {
			products: null,
			blocks: null,
			timestamp: null,
			version: '1.0'
		};
		this.cacheKey = `enhanced_collection_${this.settings.collectionHandle}`;
		this.cacheExpiry = 5 * 60 * 1000; // 5 minutes

		// Mark body as enhanced mode to prevent other scripts from initializing pagination
		document.body.classList.add("collection-enhanced-mode");
		document.body.setAttribute("data-enhanced-collection", "true");

		// Add global flag to prevent other pagination initialization
		window.ENHANCED_COLLECTION_MODE = true;

		console.log("Enhanced mode: Global enhanced collection mode flags set");

		// Initialize debouncing and tracking
		this.paginationDisabled = false;
		this.disablePaginationTimeout = null;
		this.setupMutationObserver();

		// Block problematic popstate handlers that cause AJAX reloads
		this.blockGlobalPopstateListeners();

		// Initialize components
		this.initElements();
		this.initTabNavigation();
		this.initVideoBlocks();
		this.initProductHoverImages();
		this.initURLManagement();
		this.initIntersectionObserver();
		this.initSortingIntegration();

		// Set initial state from URL and apply filtering
		this.setInitialTabFromURL();

		// Disable pagination with retry mechanism for late-loading elements
		this.disablePaginationWithRetry();

		// Set up periodic cleanup of unwanted loading states
		this.setupLoadingStateCleanup();

		// Set up periodic visibility checks
		this.setupPeriodicVisibilityChecks();

		// Initialize grid height matching for consistent rows
		this.initGridHeightMatching();
	}

	/**
	 * Block global popstate listeners that cause unwanted AJAX reloads
	 */
	blockGlobalPopstateListeners() {
		// Override the problematic popstate handler from utilities.js
		if (typeof Shopify !== "undefined" && Shopify.OptionSelectors && Shopify.OptionSelectors.HistoryState) {
			// Store original for potential restoration
			if (!this.originalHistoryStateRegister) {
				this.originalHistoryStateRegister = Shopify.OptionSelectors.HistoryState.prototype.register;
			}
			
			// Replace with no-op to prevent registration
			Shopify.OptionSelectors.HistoryState.prototype.register = function() {
				console.log("Enhanced mode: Blocked OptionSelectors popstate registration");
				return false;
			};
		}

		// Block collection AJAX that would replace the PLP (filters), but allow pagination (page 2+) fetches.
		if (typeof $ !== "undefined" && $.ajax) {
			const originalAjax = $.ajax;
			$.ajax = function(options) {
				const urlStr =
					typeof options.url === "string" ? options.url : "";
				const method = (options.type || "GET").toUpperCase();
				const isHtmlGet =
					method === "GET" &&
					(options.dataType === "html" || !options.dataType);
				const isPaginationFetch =
					isHtmlGet &&
					/[?&]page=\d+/.test(urlStr) &&
					(urlStr.includes("/collections/") || urlStr.startsWith("?"));

				if (
					options.url &&
					!isPaginationFetch &&
					(urlStr.includes("/collections/") ||
						urlStr.includes("products.json") ||
						(options.data &&
							typeof options.data === "string" &&
							options.data.includes("collection")))
				) {
					console.log(
						"Enhanced mode: Blocked potentially interfering AJAX request:",
						options.url
					);
					return {
						done: function (callback) {
							if (callback) callback("");
							return this;
						},
						fail: function (callback) {
							return this;
						},
						always: function (callback) {
							if (callback) callback("");
							return this;
						},
					};
				}
				return originalAjax.call(this, options);
			};
		}

		console.log("Enhanced mode: Global popstate interference blocked");
	}

	/**
	 * Get settings from JSON script tag
	 */
	getSettings() {
		const settingsElement = this.section.querySelector(
			"[data-collection-enhanced-settings]"
		);
		if (!settingsElement) return null;

		try {
			return JSON.parse(settingsElement.textContent);
		} catch (error) {
			console.error("Failed to parse collection enhanced settings:", error);
			return null;
		}
	}

	/**
	 * Initialize DOM elements
	 */
	initElements() {
		this.tabs = this.section.querySelectorAll(".collection-tab");
		this.grid = this.section.querySelector(".collection-enhanced__content");
		this.loadingSpinner = this.section.querySelector(
			".collection-enhanced__loading"
		);

		// Enhanced grid uses .collection-enhanced__content directly, not .block-product-grid
		this.productGrid = this.grid;

		// Store all product elements for efficient filtering
		this.allProducts = [];
		this.isFiltering = false;
		this.isSorting = false;
		this.isShowingLoading = false;
		this.storeAllProducts();

		// Remove .products-data immediately after storeAllProducts() reads it.
		// Leaving it creates duplicate form element IDs — one copy in .products-data
		// and one in the visible grid — which causes Shopify.OptionSelectors to
		// initialize the wrong form instance, breaking quick view variant swatches.
		const productsDataEl = document.querySelector('.products-data');
		if (productsDataEl) productsDataEl.remove();
	}

	/**
	 * Store all products and blocks for efficient DOM manipulation
	 */
	storeAllProducts() {
		if (!this.grid) return;

		// Get visible product elements from the grid
		const productElements = this.grid.querySelectorAll(".product-grid-item");

		// Get all blocks from the grid and store them in cache if not already cached
		if (!this.cache.blocks) {
			const blockElements = this.grid.querySelectorAll("[data-block-id]");
			this.cache.blocks = this.extractBlockData(blockElements);
			console.log(`Enhanced mode: Stored ${this.cache.blocks.length} blocks from current DOM`);
		}

		// Create map of product data from hidden data elements
		const productDataMap = new Map();
		const productDataElements = document.querySelectorAll(".product-data");
		productDataElements.forEach(dataElement => {
			const productId = dataElement.dataset.productId;
			if (productId) {
				productDataMap.set(productId, {
					productTags: dataElement.dataset.productTags || "",
					matchingTabs: dataElement.dataset.matchingTabs || "",
					position: dataElement.dataset.position || "",
				});
			}
		});

		// Combine visible elements with their data
		this.allProducts = Array.from(productElements).map(element => {
			// Try to get product ID from the element or its data attributes
			const productId =
				element.dataset.productId ||
				element.querySelector("[data-product-id]")?.dataset.productId ||
				element.querySelector("form")?.dataset.productId;

			// First try to get data from the element itself
			let matchingTabs = element.dataset.matchingTabs || "";
			let productTags = element.dataset.productTags || "";

			// If not found on element, try from separate data elements
			if (!matchingTabs) {
				const productData = productDataMap.get(productId) || {};
				matchingTabs = productData.matchingTabs || "";
				productTags = productData.productTags || "";
			}

			console.log(`Storing product ${productId} with matchingTabs:`, matchingTabs);

			return {
				element: element,
				productId: productId,
				productTags: productTags,
				matchingTabs: matchingTabs,
				originalParent: element.parentNode,
				originalNextSibling: element.nextSibling,
			};
		});
	}

	/**
	 * Initialize tab navigation
	 */
	initTabNavigation() {
		if (
			!this.tabs.length ||
			!this.settings.tabs ||
			this.settings.tabs.length === 0
		)
			return;

		this.tabs.forEach(tab => {
			tab.addEventListener("click", e => {
				e.preventDefault();
				const tabType = tab.dataset.tab;
				this.switchTab(tabType);
			});

			// Keyboard navigation
			tab.addEventListener("keydown", e => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					const tabType = tab.dataset.tab;
					this.switchTab(tabType);
				}
			});
		});
	}

	/**
	 * Switch between tabs
	 */
	switchTab(tabType) {
		if (!this.settings.tabs) return;

		// Store current tab for reference
		this.currentTab = tabType;

		// Uncheck all selected filters BEFORE switching tabs to prevent conflicts
		this.uncheckAllFilters();

		// Update active tab state
		this.tabs.forEach(tab => {
			tab.classList.remove("active");
			if (tab.dataset.tab === tabType) {
				tab.classList.add("active");
			}
		});

		try {
			// Get the new URL for server-side filtering
			const newURL = this.buildFilterURL(tabType);

			// Use cached filtering for instant response if available
			if (this.cache.products) {
				this.filterProductsWithCache(tabType);
			} else {
				// Fallback to regular client-side filtering
				this.filterProducts(tabType);
			}

			// Update URL without page reload using history.pushState
			if (window.history && window.history.pushState) {
				window.history.pushState(null, "", newURL);
			}
		} catch (error) {
			console.error("Error during tab switch:", error);
		}

		// Re-enable pagination disabled state briefly to allow new disabling
		this.paginationDisabled = false;

		// Ensure pagination stays disabled after filtering with delay
		setTimeout(() => {
			this.disablePagination();
		}, 100);

		// Reinitialize Yotpo widgets after tab switch
		this.reinitializeYotpo();
	}

	/**
	 * Build URL for server-side filtering
	 */
	buildFilterURL(tabType) {
		// Find the current tab and its tags
		const currentTab = this.settings.tabs.find(tab => tab.id === tabType);

		if (!currentTab || !currentTab.tags || currentTab.tags.length === 0) {
			// If no tags, return base collection URL
			return `/collections/${this.settings.collectionHandle}`;
		}

		// Get and clean the tags for this tab
		const cleanTags = currentTab.tags
			.map(tag => tag.trim().toLowerCase())
			.filter(tag => tag.length > 0);

		if (cleanTags.length === 0) {
			return `/collections/${this.settings.collectionHandle}`;
		}

		// Build the Shopify tag URL format
		let taggedURL;
		if (cleanTags.length === 1) {
			// Single tag: /collections/collection-name/tagname
			taggedURL = `/collections/${this.settings.collectionHandle}/${cleanTags[0]}`;
		} else {
			// Multiple tags: /collections/collection-name/tagname+tagname
			const tagString = cleanTags.join("+");
			taggedURL = `/collections/${this.settings.collectionHandle}/${tagString}`;
		}

		return taggedURL;
	}

	/**
	 * Load filtered products from server via AJAX
	 */
	loadFilteredProducts(url, tabType) {
		// Disable pagination to prevent conflicts
		this.disablePagination();

		fetch(url, {
			method: "GET",
			headers: {
				"X-Requested-With": "XMLHttpRequest",
			},
		})
			.then(response => {
				if (!response.ok) {
					throw new Error(
						`HTTP error! status: ${response.status} for URL: ${url}`
					);
				}
				return response.text();
			})
			.then(html => {
				// Parse the response HTML
				const parser = new DOMParser();
				const doc = parser.parseFromString(html, "text/html");

				// Find the new product grid content
				const newGrid = doc.querySelector(".collection-enhanced__content");

				if (newGrid && this.grid) {
					// Replace the current grid content with new filtered content
					this.grid.innerHTML = newGrid.innerHTML;

					// Reinitialize after server-side load
					this.storeAllProducts();

					// Skip GridInterleaver for enhanced collections - our reflowProducts handles positioning
					// GridInterleaver removes blocks and doesn't properly handle position_after: 0
					console.log("🔍 DEBUG: Skipping GridInterleaver during tab change - using reflowProducts positioning instead");

					// Use reflowProducts to handle block positioning after tab change
					const visibleProducts = Array.from(this.grid.querySelectorAll(".product-grid-item"));
					this.reflowProducts(visibleProducts);

					// Keep existing hooks
					this.reinitializePagination();
					this.initProductHoverImages();
					this.initVideoBlocks();

					// Match row heights after content change
					if (this.matchRowHeights) {
						setTimeout(() => this.matchRowHeights(), 100);
					}

					// Update URL in browser
					this.updateURL(tabType);

					// Reinitialize hover effects, pagination, and other features
					this.initProductHoverImages();
					this.reinitializePagination();
					this.hideLoading();
				} else {
					// Handle empty results
					this.handleEmptyFilterState({ id: tabType });
					this.hideLoading();
				}
			})
			.catch(error => {
				console.error("Error loading filtered products:", error);
				this.hideLoading();
				this.enablePagination(); // Re-enable pagination on error
				// Fallback to page refresh
				window.location.href = url;
			});
	}

	/**
	 * Set up mutation observer to catch late-loading pagination elements
	 */
	setupMutationObserver() {
		// Create mutation observer to watch for pagination elements being added
		this.mutationObserver = new MutationObserver(mutations => {
			const pt = this.paginationType;
			if (
				pt === "infinite_scroll" ||
				pt === "load_more" ||
				pt === "infinite_load_more"
			) {
				return;
			}

			let foundPagination = false;

			mutations.forEach(mutation => {
				mutation.addedNodes.forEach(node => {
					if (node.nodeType === Node.ELEMENT_NODE) {
						// Check if added node is pagination or contains pagination
						const paginationSelectors = [
							"[data-load-infinite]",
							"[data-load-more]",
							"[data-load-more-infinite]",
							"[data-load-infinite-scroll]",
							"[data-custom-pagination]",
							".load-more",
							".pagination-button",
							".container--pagination",
						];

						const isPagination = paginationSelectors.some(
							selector =>
								node.matches &&
								(node.matches(selector) || node.querySelector(selector))
						);

						if (isPagination) {
							console.log(
								"Enhanced mode: New pagination elements detected by mutation observer, disabling immediately..."
							);
							foundPagination = true;

							// Immediately disable new pagination elements
							const newPaginationElements = node.matches
								? [node]
								: Array.from(
										node.querySelectorAll(
											paginationSelectors.join(", ")
										)
								  );
							newPaginationElements.forEach(element => {
								if (
									element.closest(
										".collection-main, .collection__main, .container--pagination, .collection-enhanced"
									)
								) {
									console.log(
										"Enhanced mode: Immediately hiding new pagination element:",
										element
									);
									element.style.display = "none";
									element.disabled = true;
								}
							});
						}
					}
				});
			});

			if (foundPagination) {
				// Force stop loading and disable pagination again
				this.forceStopLoading();

				// Reset pagination disabled flag to allow re-disabling
				this.paginationDisabled = false;

				// Debounce to avoid excessive calls but still disable
				clearTimeout(this.disablePaginationTimeout);
				this.disablePaginationTimeout = setTimeout(() => {
					this.disablePagination();
				}, 50);
			}
		});

		// Start observing
		this.mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["class", "style"],
		});
	}

	/**
	 * Disable pagination with retry mechanism for late-loading elements
	 */
	disablePaginationWithRetry() {
		// Initial disable
		this.disablePagination();

		// Retry after short delays to catch late-loading elements
		setTimeout(() => this.disablePagination(), 500);
		setTimeout(() => this.disablePagination(), 1000);
		setTimeout(() => this.disablePagination(), 2000);
	}

	/**
	 * Disable pagination to prevent conflicts during filtering (enhanced mode only)
	 */
	disablePagination() {
		// Prevent rapid successive calls
		if (this.paginationDisabled) {
			console.log("Enhanced mode: Pagination already disabled, skipping");
			return;
		}

		// Only disable pagination in enhanced mode
		if (!this.section) return;

		const paginationType =
			this.section.getAttribute("data-pagination-type") || "";
		if (
			paginationType === "infinite_scroll" ||
			paginationType === "load_more" ||
			paginationType === "infinite_load_more"
		) {
			return;
		}

		console.log("Enhanced mode: Disabling pagination during filtering");

		// Search for pagination elements globally, not just within enhanced section
		// This is necessary because pagination is often outside the enhanced section
		const paginationSelectors = [
			"[data-load-infinite]",
			"[data-load-more]",
			"[data-load-more-infinite]",
			"[data-load-infinite-scroll]",
			"[data-custom-pagination]",
			".load-more",
			".pagination-button",
			".pagination-button__load-more",
			".pagination-button__load-more-infinite",
			"[data-load-more--grid]",
		];

		// Find pagination elements globally
		const allPaginationButtons = document.querySelectorAll(
			paginationSelectors.join(", ")
		);

		console.log(
			`Enhanced mode: Found ${allPaginationButtons.length} pagination elements to disable`
		);

		let disabledCount = 0;
		allPaginationButtons.forEach(button => {
			// Only disable if it's related to collections (avoid affecting other paginations)
			const isCollectionPagination = button.closest(
				".collection-main, .collection__main, .container--pagination, .collection-enhanced"
			);
			if (isCollectionPagination) {
				button.style.display = "none";
				button.disabled = true;
				// Store original state for restoration
				if (!button.dataset.originalDisplay) {
					button.dataset.originalDisplay = button.style.display || "";
				}
				if (!button.dataset.originalDisabled) {
					button.dataset.originalDisabled = button.disabled
						? "true"
						: "false";
				}
				disabledCount++;
			}
		});

		console.log(
			`Enhanced mode: Actually disabled ${disabledCount} pagination elements`
		);

		// Disable Waypoint.Infinite more safely
		if (typeof Waypoint !== "undefined" && Waypoint.getAllWaypoints) {
			try {
				const existingWaypoints = Waypoint.getAllWaypoints();
				if (Array.isArray(existingWaypoints)) {
					existingWaypoints.forEach(waypoint => {
						try {
							// Safely check waypoint properties before destroying
							if (waypoint && waypoint.element && waypoint.destroy) {
								// Check if this waypoint is collection-related
								const waypointElement = waypoint.element;
								const isCollectionWaypoint =
									waypointElement &&
									(waypointElement.closest(
										".collection-main, .collection__main, .collection-enhanced"
									) ||
										waypointElement.matches(
											"[data-load-infinite], [data-load-more]"
										));

								if (isCollectionWaypoint) {
									console.log(
										"Enhanced mode: Destroying collection-related waypoint"
									);
									waypoint.destroy();
								}
							}
						} catch (waypointError) {
							console.warn(
								"Enhanced mode: Error destroying waypoint:",
								waypointError
							);
						}
					});
				}
			} catch (waypointError) {
				console.warn(
					"Enhanced mode: Error accessing Waypoint:",
					waypointError
				);
			}
		}

		// Prevent Waypoint constructor errors by temporarily disabling Waypoint
		if (typeof Waypoint !== "undefined") {
			// Store original constructor
			if (!this.originalWaypointConstructor) {
				this.originalWaypointConstructor = Waypoint;
			}

			// Temporarily replace Waypoint constructor to prevent errors
			window.Waypoint = function (options) {
				console.warn(
					"Enhanced mode: Waypoint construction blocked during filtering"
				);
				return { destroy: function () {} }; // Return dummy object
			};

			// Copy static methods
			for (let prop in this.originalWaypointConstructor) {
				if (this.originalWaypointConstructor.hasOwnProperty(prop)) {
					window.Waypoint[prop] = this.originalWaypointConstructor[prop];
				}
			}
		}

		// More aggressive infinite scroll disabling with method override
		if (typeof Shopify !== "undefined" && Shopify.theme) {
			// Disable infinite scroll completely
			if (Shopify.theme.infiniteScroll) {
				try {
					console.log("Enhanced mode: Unloading Shopify infinite scroll");
					if (Shopify.theme.infiniteScroll.unload) {
						Shopify.theme.infiniteScroll.unload();
					}
				} catch (infiniteScrollError) {
					console.warn(
						"Enhanced mode: Error unloading infinite scroll:",
						infiniteScrollError
					);
				}
			}

			// Also disable any load more functionality
			if (Shopify.theme.loadMore) {
				try {
					console.log("Enhanced mode: Disabling load more");
					if (Shopify.theme.loadMore.unload) {
						Shopify.theme.loadMore.unload();
					}
				} catch (loadMoreError) {
					console.warn(
						"Enhanced mode: Error disabling load more:",
						loadMoreError
					);
				}
			}

			// Create the no-op objects first
			const noOpInfiniteScroll = {
				init: function () {
					console.log(
						"Enhanced mode: infiniteScroll.init() blocked - enhanced mode active"
					);
					return false;
				},
				unload: function () {
					console.log(
						"Enhanced mode: infiniteScroll.unload() blocked - enhanced mode active"
					);
					return false;
				},
				enableInfinite: function () {
					console.log(
						"Enhanced mode: infiniteScroll.enableInfinite() blocked - enhanced mode active"
					);
					return false;
				},
				loadNextPage: function () {
					console.log(
						"Enhanced mode: infiniteScroll.loadNextPage() blocked - enhanced mode active"
					);
					return false;
				},
			};

			const noOpLoadMore = {
				init: function () {
					console.log(
						"Enhanced mode: loadMore.init() blocked - enhanced mode active"
					);
					return false;
				},
				unload: function () {
					console.log(
						"Enhanced mode: loadMore.unload() blocked - enhanced mode active"
					);
					return false;
				},
			};

			// Override properties safely - check if they can be redefined first
			try {
				// Try to delete the property first if it exists
				if ('infiniteScroll' in Shopify.theme) {
					delete Shopify.theme.infiniteScroll;
				}
				Object.defineProperty(Shopify.theme, "infiniteScroll", {
					value: noOpInfiniteScroll,
					writable: false,
					configurable: false,
				});
			} catch (e) {
				// If we can't redefine it, just try to override it directly
				console.warn("Enhanced mode: Cannot redefine infiniteScroll, attempting direct override");
				try {
					Shopify.theme.infiniteScroll = noOpInfiniteScroll;
				} catch (e2) {
					console.warn("Enhanced mode: Cannot override infiniteScroll at all:", e2);
				}
			}

			try {
				// Try to delete the property first if it exists
				if ('loadMore' in Shopify.theme) {
					delete Shopify.theme.loadMore;
				}
				Object.defineProperty(Shopify.theme, "loadMore", {
					value: noOpLoadMore,
					writable: false,
					configurable: false,
				});
			} catch (e) {
				// If we can't redefine it, just try to override it directly
				console.warn("Enhanced mode: Cannot redefine loadMore, attempting direct override");
				try {
					Shopify.theme.loadMore = noOpLoadMore;
				} catch (e2) {
					console.warn("Enhanced mode: Cannot override loadMore at all:", e2);
				}
			}

			console.log(
				"Enhanced mode: Shopify pagination objects replaced with no-op methods"
			);
		}

		// Block Waypoint library pagination if it exists
		if (typeof Waypoint !== "undefined") {
			// Override Waypoint.Infinite constructor to prevent pagination
			const originalWaypointInfinite = Waypoint.Infinite;
			Waypoint.Infinite = function (options) {
				console.log(
					"Enhanced mode: Waypoint.Infinite() blocked - enhanced mode active",
					options
				);
				// Return a dummy object that won't interfere
				return {
					destroy: function () {
						return false;
					},
					enable: function () {
						return false;
					},
					disable: function () {
						return false;
					},
				};
			};
			console.log("Enhanced mode: Waypoint.Infinite constructor blocked");
		}

		// Block any jQuery-based pagination plugins
		if (typeof $ !== "undefined") {
			// Override any pagination-related jQuery methods
			$.fn.infiniteScroll = function () {
				console.log(
					"Enhanced mode: jQuery infiniteScroll plugin blocked - enhanced mode active"
				);
				return this;
			};

			$.fn.waypoint = function () {
				console.log(
					"Enhanced mode: jQuery waypoint plugin blocked - enhanced mode active"
				);
				return this;
			};

			console.log("Enhanced mode: jQuery pagination plugins blocked");
		}

		// Remove or disable pagination DOM elements that app.js detects
		if (typeof $ !== "undefined") {
			// Remove data-custom-pagination attributes that app.js checks for
			$("[data-custom-pagination]").removeAttr("data-custom-pagination");

			// Remove any pagination-related classes that might trigger initialization
			$(".pagination, .infinite-scroll, .load-more").removeClass(
				"pagination infinite-scroll load-more"
			);

			// Hide any pagination containers to prevent visual interference
			$(
				".pagination__container, .infinite-scroll__container, .load-more__container"
			).hide();

			console.log("Enhanced mode: Pagination DOM elements disabled");
		}

		// Set up continuous monitoring for any new pagination elements
		if (typeof MutationObserver !== "undefined") {
			const paginationObserver = new MutationObserver(function (mutations) {
				mutations.forEach(function (mutation) {
					if (mutation.type === "childList") {
						mutation.addedNodes.forEach(function (node) {
							if (node.nodeType === 1) {
								// Element node
								// Check if the added node has pagination attributes
								if (
									node.hasAttribute &&
									node.hasAttribute("data-custom-pagination")
								) {
									node.removeAttribute("data-custom-pagination");
									console.log(
										"Enhanced mode: New pagination element detected and disabled"
									);
								}

								// Check child elements for pagination attributes
								const paginationElements = node.querySelectorAll
									? node.querySelectorAll("[data-custom-pagination]")
									: [];
								paginationElements.forEach(function (el) {
									el.removeAttribute("data-custom-pagination");
									console.log(
										"Enhanced mode: Child pagination element detected and disabled"
									);
								});
							}
						});
					}
				});
			});

			// Start observing the entire document
			paginationObserver.observe(document.body, {
				childList: true,
				subtree: true,
			});

			console.log("Enhanced mode: Pagination mutation observer active");
		}

		// Set up product visibility protection observer
		this.setupProductVisibilityObserver();

		// Override common hiding methods to prevent products from being hidden
		this.overrideHidingMethods();

		// Force all products to be visible and prevent hiding
		this.forceProductsVisible();

		// Add comprehensive debugging for product visibility
		this.addProductVisibilityDebugging();

		// Set up periodic visibility checks
		this.setupPeriodicVisibilityChecks();

		// Remove loading classes and forcefully stop loading states
		this.forceStopLoading();

		// Enhanced product visibility protection
		this.ensureProductsVisible();

		// Inject CSS to override any pagination hiding styles
		this.injectProductVisibilityCSS();

		// Force refresh product visibility after a delay to catch any late changes
		setTimeout(() => {
			this.ensureProductsVisible();
			console.log(
				"Enhanced mode: Delayed product visibility check completed"
			);
		}, 100);

		// Additional delay for any AJAX or dynamic content
		setTimeout(() => {
			this.ensureProductsVisible();
			console.log("Enhanced mode: Final product visibility check completed");
		}, 500);

		// Disable any intersection observers
		if (this.paginationObserver) {
			this.paginationObserver.disconnect();
		}

		this.paginationDisabled = true;
	}

	/**
	 * Enable pagination after filtering completes
	 */
	enablePagination() {
		console.log("Enhanced mode: Re-enabling pagination");

		// Restore Waypoint constructor if it was disabled
		if (this.originalWaypointConstructor) {
			window.Waypoint = this.originalWaypointConstructor;
			this.originalWaypointConstructor = null;
		}

		// Restore pagination elements from stored original state
		const paginationSelectors = [
			"[data-load-infinite]",
			"[data-load-more]",
			"[data-load-more-infinite]",
			"[data-load-infinite-scroll]",
			"[data-custom-pagination]",
			".load-more",
			".pagination-button",
			".pagination-button__load-more",
			".pagination-button__load-more-infinite",
			"[data-load-more--grid]",
		];

		const allPaginationButtons = document.querySelectorAll(
			paginationSelectors.join(", ")
		);

		allPaginationButtons.forEach(button => {
			// Only restore collection-related pagination
			const isCollectionPagination = button.closest(
				".collection-main, .collection__main, .container--pagination, .collection-enhanced"
			);
			if (isCollectionPagination) {
				// Restore original display state
				if (button.dataset.originalDisplay !== undefined) {
					button.style.display = button.dataset.originalDisplay;
					delete button.dataset.originalDisplay;
				}

				// Restore original disabled state
				if (button.dataset.originalDisabled !== undefined) {
					button.disabled = button.dataset.originalDisabled === "true";
					delete button.dataset.originalDisabled;
				}
			}
		});

		this.paginationDisabled = false;
	}

	/**
	 * Reinitialize pagination after AJAX content load
	 */
	reinitializePagination() {
		// Re-enable pagination first
		this.enablePagination();

		// Check if infinite scroll pagination exists and reinitialize it
		if (
			typeof Shopify !== "undefined" &&
			Shopify.theme &&
			Shopify.theme.infiniteScroll &&
			document.querySelector("[data-custom-pagination]") &&
			!this.paginationDisabled
		) {
			// Reinitialize infinite scroll for new content
			Shopify.theme.infiniteScroll.init();
		}
	}

	/**
	 * Filter products based on tab selection with dynamic repositioning
	 */
	filterProducts(tabType) {
		if (!this.grid || !this.allProducts) return;

		// Prevent multiple simultaneous filtering operations
		if (this.isFiltering) {
			return;
		}

		this.isFiltering = true;

		try {
			// Find the tab configuration
			const currentTab = this.settings.tabs.find(tab => tab.id === tabType);

			// Determine which products to show
			const shouldShowAll =
				!currentTab || !currentTab.tags || currentTab.tags.length === 0;

			// Hide all products first with smooth transition
			this.allProducts.forEach(product => {
				const element = product.element;
				element.style.opacity = "0";
				element.style.transform = "translateY(-10px)";
				element.style.transition = "opacity 0.2s ease, transform 0.2s ease";
				
				// Actually hide after transition
				setTimeout(() => {
					element.style.display = "none";
					element.style.opacity = "";
					element.style.transform = "";
					element.style.transition = "";
				}, 200);
			});

			// Get products to show
			let productsToShow = [];

			if (shouldShowAll) {
				productsToShow = this.allProducts;
			} else {
				productsToShow = this.allProducts.filter(product => {
					const matchingTabs = product.matchingTabs || "";
					const matches = matchingTabs
						.split(",")
						.map(tab => tab.trim())
						.includes(tabType);
					return matches;
				});
			}

			// Handle edge case: no products match filter
			if (productsToShow.length === 0) {
				this.handleEmptyFilterState(currentTab);
				return;
			}

			// Show filtered products with smooth transitions after hiding completes
			setTimeout(() => {
				productsToShow.forEach((product, index) => {
					const element = product.element;
					
					// Set initial state for animation
					element.style.opacity = "0";
					element.style.transform = "translateY(20px)";
					element.style.transition = "opacity 0.3s ease, transform 0.3s ease";
					element.style.transitionDelay = `${index * 0.05}s`;
					element.style.display = "";

					// Trigger animation
					setTimeout(() => {
						element.style.opacity = "1";
						element.style.transform = "translateY(0)";
					}, 50);

					// Clean up transition styles after animation
					setTimeout(() => {
						element.style.transition = "";
						element.style.transform = "";
						element.style.transitionDelay = "";
					}, 300 + (index * 50));
				});

				// Use reflowProducts to properly position blocks relative to filtered products
				this.reflowProducts(productsToShow.map(p => p.element));

				// Match row heights after reflow
				if (this.matchRowHeights) {
					setTimeout(() => this.matchRowHeights(), 100);
				}
			}, 220); // Wait for hide animation to complete

			// Check if featured product block exists after reflow
			const featuredProductAfterReflow = this.grid.querySelector('[data-block-id*="featured_product"]');
			console.log("🔍 DEBUG: Featured product block after reflow:", featuredProductAfterReflow ? "EXISTS" : "MISSING");
			
			// Skip GridInterleaver for enhanced collections - our reflowProducts handles positioning
			// GridInterleaver removes blocks and doesn't properly handle position_after: 0
			console.log("🔍 DEBUG: Skipping GridInterleaver - using reflowProducts positioning instead");

			// Update ARIA label for screen readers
			this.grid.setAttribute(
				"aria-label",
				`${productsToShow.length} products displayed`
			);

			// Reinitialize hover effects for visible products
			setTimeout(() => {
				this.initProductHoverImages();
				// Force stop any loading states after filtering
				this.forceStopLoading();
			}, 150);
		} catch (error) {
			console.error("Error during filtering:", error);
		} finally {
			this.isFiltering = false;
			// Ensure loading is stopped even on error
			this.forceStopLoading();
		}
	}

	/**
	 * Reflow products and dynamically reposition blocks based on visible product count
	 */
	reflowProducts(visibleProducts) {
		console.log("🔍 DEBUG: reflowProducts called with", visibleProducts.length, "visible products");
		if (!this.grid || !visibleProducts.length) {
			console.log("🔍 DEBUG: reflowProducts early return - no grid or no visible products");
			return;
		}

		// Get all blocks from the grid
		const blocks = Array.from(this.grid.children).filter(item =>
			item.hasAttribute("data-block-id")
		);
		console.log("🔍 DEBUG: Found blocks in grid:", blocks.map(b => ({
			id: b.dataset.blockId,
			type: b.className,
			position: b.dataset.position,
			originalPosition: b.dataset.originalPositionAfter
		})));

		// Store original block configurations for repositioning
		const blockConfigs = blocks.map(block => {
			const blockType = this.getBlockType(block);
			// Extract original position_after from data attribute
			let positionAfter = parseInt(block.dataset.originalPositionAfter);

			// If originalPositionAfter is not stored, this means the block lost its position data
			// We need to restore it from the server-side rendered position
			if (isNaN(positionAfter)) {
				console.log("🔍 DEBUG: Block", block.dataset.blockId, "missing originalPositionAfter, skipping...");
				// Don't try to guess - this block might be corrupted/duplicated
				return null;
			}

			console.log("🔍 DEBUG: Block config for", block.dataset.blockId, ":", {
				blockType,
				positionAfter,
				currentPosition: block.dataset.position
			});

			return {
				element: block,
				positionAfter: positionAfter,
				type: blockType,
			};
		}).filter(config => config !== null); // Remove null configs

		// Create new positioning array with products and recalculated blocks
		const newOrder = [];
		
		// Count blocks with position_after: 0 to determine product offset
		const zeroPositionBlocks = blockConfigs.filter(config => config.positionAfter === 0);
		const zeroBlockCount = zeroPositionBlocks.length;
		
		// Debug: only featured_product should have position_after: 0
		console.log("🔍 DEBUG: Found", zeroBlockCount, "blocks with position_after: 0:", zeroPositionBlocks.map(b => b.element.dataset.blockId));
		if (zeroBlockCount > 1) {
			console.log("🚨 DEBUG: Multiple blocks have position_after: 0! This shouldn't happen:");
			zeroPositionBlocks.forEach(b => {
				console.log(`  - ${b.element.dataset.blockId}: originalPositionAfter = ${b.element.dataset.originalPositionAfter}, current position = ${b.element.dataset.position}`);
			});
		}
		
		// First, add blocks with position_after: 0 (should appear at the very beginning)
		zeroPositionBlocks.forEach((blockConfig, index) => {
			// Give sequential positions starting from 1
			const newBlockPosition = index + 1;
			
			// Update block's position data
			blockConfig.element.dataset.position = newBlockPosition;

			newOrder.push({
				element: blockConfig.element,
				position: newBlockPosition,
				type: "block",
			});
		});

		// Then add products with their sequential positions, offset by zero-position blocks
		visibleProducts.forEach((product, index) => {
			const newPosition = index + 1 + zeroBlockCount;
			// Update product's position to reflect new filtered sequence
			product.dataset.filteredPosition = newPosition;
			newOrder.push({
				element: product,
				position: newPosition,
				type: "product",
			});

			// Check if any blocks should be inserted after this product
			blockConfigs.forEach(blockConfig => {
				if (blockConfig.positionAfter === newPosition) {
					// Calculate new block position (right after this product)
					const newBlockPosition = newPosition + 1;

					// Update block's position data
					blockConfig.element.dataset.position = newBlockPosition;

					newOrder.push({
						element: blockConfig.element,
						position: newBlockPosition,
						type: "block",
					});
				}
			});
		});

		// Handle blocks that are positioned beyond the last visible product
		// Move them to the end instead of leaving them at the beginning
		blockConfigs.forEach(blockConfig => {
			// Skip blocks that are already positioned (position_after: 0 or within product range)
			const alreadyPositioned = newOrder.some(item => item.element === blockConfig.element);
			if (alreadyPositioned) {
				return;
			}

			// This block's target position is beyond visible products - move to end
			if (blockConfig.positionAfter > visibleProducts.length) {
				const endPosition = visibleProducts.length + 1 + zeroBlockCount + 
					(newOrder.filter(item => item.type === "block" && item.position > visibleProducts.length + zeroBlockCount).length);

				// Update block's position data
				blockConfig.element.dataset.position = endPosition;

				newOrder.push({
					element: blockConfig.element,
					position: endPosition,
					type: "block",
				});

				console.log(`🔍 DEBUG: Moved out-of-range block ${blockConfig.element.dataset.blockId} from position_after ${blockConfig.positionAfter} to end position ${endPosition}`);
			}
		});

		// Sort by position to maintain proper order
		newOrder.sort((a, b) => a.position - b.position);
		console.log("🔍 DEBUG: Final newOrder:", newOrder.map(item => ({
			type: item.type,
			position: item.position,
			id: item.element.dataset.blockId || item.element.dataset.productId || 'unknown'
		})));

		// Clear grid and rebuild with new order
		const fragment = document.createDocumentFragment();
		newOrder.forEach(item => {
			console.log("🔍 DEBUG: Adding to fragment:", item.type, "at position", item.position);
			if (item.element && item.element.parentNode === this.grid) {
				fragment.appendChild(item.element);
			} else if (item.element) {
				console.log("🔍 DEBUG: Element not in grid, adding anyway:", item.element.dataset.blockId || item.element.dataset.productId);
				fragment.appendChild(item.element);
			}
		});

		// Append all items back to the grid in the correct order
		this.grid.appendChild(fragment);
		console.log("🔍 DEBUG: reflowProducts completed. Grid now has", this.grid.children.length, "children");
		
		// Debug what's actually in the grid
		const allGridChildren = Array.from(this.grid.children);
		console.log("🔍 DEBUG: Grid children after reflow:", allGridChildren.map(child => ({
			type: child.classList.contains('product-grid-item') ? 'product' : 'block',
			id: child.dataset.blockId || child.dataset.productId || 'unknown',
			classes: child.className,
			display: child.style.display
		})));
		
		// Ensure all blocks are visible (remove any debug styling)
		const allBlocks = this.grid.querySelectorAll('[data-block-id]');
		allBlocks.forEach(block => {
			block.style.display = "";
			block.style.visibility = "";
			block.style.opacity = "";
			block.style.backgroundColor = "";
			block.style.border = "";
			block.style.minHeight = "";
			block.style.zIndex = "";
		});
	}

	/**
	 * Get the type of a block element
	 */
	getBlockType(blockElement) {
		if (blockElement.classList.contains("block-video")) return "video";
		if (blockElement.classList.contains("block-content-image-text"))
			return "content_image_text";
		if (blockElement.classList.contains("block-content-text"))
			return "content_text";
		if (blockElement.classList.contains("block-featured-product"))
			return "featured_product";
		if (blockElement.classList.contains("block-image-overlay"))
			return "image_overlay";
		
		// Debug unknown types
		console.log("🔍 DEBUG: Unknown block type for element:", blockElement.className, blockElement.dataset.blockId);
		return "unknown";
	}

	/**
	 * Handle empty filter state when no products match
	 */
	handleEmptyFilterState(currentTab) {
		// All products are already removed from DOM by filterProducts method

		// Check if empty state message already exists
		let emptyStateElement = this.grid.querySelector(
			".collection-enhanced__empty-state"
		);

		if (!emptyStateElement) {
			// Create empty state message
			emptyStateElement = document.createElement("div");
			emptyStateElement.className = "collection-enhanced__empty-state";
			emptyStateElement.innerHTML = `
				<div class="empty-state__content">
					<h3>No products found</h3>
					<p>No products match the selected filter "${
						currentTab?.label || "criteria"
					}". Try selecting a different category or view all products.</p>
				</div>
			`;

			this.grid.appendChild(emptyStateElement);
		}

		// Show the empty state
		emptyStateElement.style.display = "";

		// Update ARIA label
		this.grid.setAttribute("aria-label", "No products found");
	}

	/**
	 * Show loading state
	 */
	showLoading() {
		// Completely disable loading spinner for filter operations since they're instantaneous
		// We only need loading for AJAX operations (server-side filtering)
		console.log(
			"Loading state request blocked - filters are applied instantly",
			new Date().toISOString()
		);
		return;
	}

	/**
	 * Hide loading state
	 */
	hideLoading() {
		this.isShowingLoading = false;
		console.log("Loading state hidden", new Date().toISOString());
		console.trace("hideLoading called from:");

		if (this.loadingSpinner) {
			this.loadingSpinner.classList.remove("active");
		}
		if (this.grid) {
			this.grid.classList.remove("filtering");
		}

		// Ensure no external loading classes are lingering (scoped to enhanced section)
		if (this.section) {
			this.section.classList.remove("loading-in-progress");
		}
		if (this.grid) {
			this.grid.classList.remove("loading-in-progress");
		}
	}

	/**
	 * Forcefully stop all loading states and show products
	 */
	forceStopLoading() {
		console.log("Enhanced mode: Force stopping all loading states");

		// Remove loading classes from enhanced section
		if (this.section) {
			this.section.classList.remove("loading-in-progress", "loading");
		}
		if (this.grid) {
			this.grid.classList.remove(
				"loading-in-progress",
				"loading",
				"filtering"
			);
		}

		const keepPaginationSentinel =
			this.paginationType === "infinite_scroll" ||
			this.paginationType === "load_more" ||
			this.paginationType === "infinite_load_more";

		// Hide all loading spinners globally
		const loadingElements = document.querySelectorAll(
			".is-loading, .loading-spinner, .collection__loading-icon"
		);
		loadingElements.forEach(element => {
			if (
				keepPaginationSentinel &&
				(element.matches("[data-load-infinite], [data-load-infinite-scroll]") ||
					element.closest("[data-load-infinite], [data-load-infinite-scroll]"))
			) {
				return;
			}
			if (
				element.closest(
					".collection-main, .collection__main, .collection-enhanced, .container--pagination"
				)
			) {
				element.style.display = "none";
				element.classList.remove("is-loading", "is-loading--icon-only");
			}
		});

		// Ensure all products are visible in enhanced mode
		if (this.grid) {
			const products = this.grid.querySelectorAll(
				".product-grid-item, .product__thumbnail"
			);
			products.forEach(product => {
				// Only show products that aren't filtered out
				if (!product.style.display || product.style.display !== "none") {
					product.style.opacity = "1";
					product.style.visibility = "visible";
				}
			});
		}

		if (!keepPaginationSentinel) {
			const paginationButtons = document.querySelectorAll(
				"[data-load-infinite], [data-load-more], .is-loading"
			);
			paginationButtons.forEach(button => {
				if (button.closest(".container--pagination")) {
					button.style.display = "none";
				}
			});
		}
	}

	/**
	 * Set up periodic cleanup of unwanted loading states from external scripts
	 */
	setupLoadingStateCleanup() {
		// Aggressively clean up any lingering loading states every second (enhanced mode only)
		this.cleanupInterval = setInterval(() => {
			// Always remove loading classes since we don't use loading spinners for filtering anymore
			if (this.section) {
				this.section.classList.remove("loading-in-progress");
			}
			if (this.grid) {
				this.grid.classList.remove("loading-in-progress", "filtering");
			}

			// Always ensure our enhanced collection spinner is hidden
			if (
				this.loadingSpinner &&
				this.loadingSpinner.classList.contains("active")
			) {
				console.log(
					"Cleaning up stray enhanced collection loading spinner",
					new Date().toISOString()
				);
				this.loadingSpinner.classList.remove("active");
			}
		}, 1000); // Check every 1 second for aggressive cleanup
	}

	/**
	 * Initialize grid height matching for consistent row heights
	 */
	initGridHeightMatching() {
		const gridContent = this.section.querySelector('.collection-enhanced__content');
		if (!gridContent) return;

		const matchRowHeights = () => {
			// Get all visible product items to determine standard height
			const productItems = Array.from(gridContent.querySelectorAll('.product-grid-item')).filter(item => 
				item.offsetParent !== null // Only visible items
			);

			if (productItems.length === 0) return;

			// Calculate the typical product height (use the first visible product as reference)
			// Products in the same row should have similar heights
			let productHeight = 0;
			const productHeights = productItems.slice(0, 3).map(item => 
				item.getBoundingClientRect().height
			);
			
			// Use the maximum height from the first few products as the standard
			productHeight = Math.max(...productHeights);

			// Reset heights for all blocks first
			const allBlocks = gridContent.querySelectorAll(
				'.block-featured-product, .block-image-overlay, .block-content-text'
			);
			allBlocks.forEach(block => {
				block.style.minHeight = '';
			});

			// Apply product height to blocks that should match
			// These blocks appear alongside products and should have the same height
			const blocksToMatch = gridContent.querySelectorAll(
				'.block-featured-product, ' +  // Spans 2 columns, should match product height
				'.block-image-overlay, ' +      // Spans 2 columns, should match product height
				'.block-content-text'            // Single column, should match product height
			);

			blocksToMatch.forEach(block => {
				// Only apply to visible blocks
				if (block.offsetParent !== null) {
					block.style.minHeight = `${productHeight}px`;
				}
			});

			// Full-width blocks (.block-video, .block-content-image-text) maintain their natural height
			// They span the full row so don't need height matching
		};

		// Run on initial load
		setTimeout(matchRowHeights, 100);

		// Run on window resize
		let resizeTimer;
		window.addEventListener('resize', () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(matchRowHeights, 250);
		});

		// Run after tab changes
		this.section.addEventListener('collection:tabChanged', () => {
			setTimeout(matchRowHeights, 300);
		});

		// Run after filtering
		this.section.addEventListener('collection:filtered', () => {
			setTimeout(matchRowHeights, 300);
		});

		// Store for later use
		this.matchRowHeights = matchRowHeights;
	}

	/**
	 * Initialize product hover image effects
	 */
	initProductHoverImages() {
		const products = this.section.querySelectorAll(
			".product__thumbnail.has-hover-image"
		);

		products.forEach(product => {
			const hoverImage = product.querySelector(".hover-image");
			if (!hoverImage) return;

			// Preload hover image on hover intent
			let preloadTimer;

			product.addEventListener("mouseenter", () => {
				if (!hoverImage.complete) {
					preloadTimer = setTimeout(() => {
						// Image should already be loading due to lazy loading
					}, 100);
				}
			});

			product.addEventListener("mouseleave", () => {
				if (preloadTimer) {
					clearTimeout(preloadTimer);
				}
			});
		});

		// Initialize swatch hover functionality
		this.initSwatchHover();
	}

	/**
	 * Initialize swatch hover functionality
	 */
	initSwatchHover() {
		const swatches = this.section.querySelectorAll(".swatch[data-image]");

		swatches.forEach(swatch => {
			const productContainer = swatch.closest(".product__thumbnail");
			if (!productContainer) return;

			const primaryImage = productContainer.querySelector(".primary-image");
			if (!primaryImage) return;

			// Store original image source
			if (!primaryImage.dataset.originalSrc) {
				primaryImage.dataset.originalSrc = primaryImage.src;
			}

			// Preload variant image on hover intent
			let preloadTimer;
			let variantImage = null;

			swatch.addEventListener("mouseenter", () => {
				const variantImageSrc = swatch.dataset.image;
				if (!variantImageSrc) return;

				// Preload the variant image
				preloadTimer = setTimeout(() => {
					if (!variantImage) {
						variantImage = new Image();
						variantImage.onload = () => {
							// Smooth transition to variant image
							primaryImage.style.opacity = "0";
							setTimeout(() => {
								primaryImage.src = variantImageSrc;
								primaryImage.style.opacity = "1";
							}, 150);
						};
						variantImage.onerror = () => {
							// Keep original image on error
							console.warn(
								"Failed to load variant image:",
								variantImageSrc
							);
						};
						variantImage.src = variantImageSrc;
					} else {
						// Image already loaded, immediate transition
						primaryImage.style.opacity = "0";
						setTimeout(() => {
							primaryImage.src = variantImageSrc;
							primaryImage.style.opacity = "1";
						}, 150);
					}
				}, 50); // Quick preload for immediate feedback
			});

			swatch.addEventListener("mouseleave", () => {
				if (preloadTimer) {
					clearTimeout(preloadTimer);
				}

				// Return to original image
				const originalSrc = primaryImage.dataset.originalSrc;
				if (originalSrc && primaryImage.src !== originalSrc) {
					primaryImage.style.opacity = "0";
					setTimeout(() => {
						primaryImage.src = originalSrc;
						primaryImage.style.opacity = "1";
					}, 150);
				}
			});
		});
	}

	/**
	 * Initialize video blocks
	 */
	initVideoBlocks() {
		const videoBlocks = this.section.querySelectorAll(".video-block");

		videoBlocks.forEach(block => {
			this.initYouTubeVideo(block);
			this.initUploadedVideo(block);
		});
	}

	/**
	 * Initialize YouTube video functionality
	 */
	initYouTubeVideo(block) {
		const playButton = block.querySelector("[data-play-youtube]");
		const videoId = block.querySelector("[data-video-id]")?.dataset.videoId;
		const iframeContainer = block.querySelector("[data-iframe-container]");
		const poster = block.querySelector("[data-poster]");

		if (!playButton || !videoId || !iframeContainer) return;

		playButton.addEventListener("click", () => {
			// Create iframe
			const iframe = document.createElement("iframe");
			iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&modestbranding=1`;
			iframe.title = "YouTube video player";
			iframe.setAttribute("frameborder", "0");
			iframe.allow =
				"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
			iframe.allowFullscreen = true;
			iframe.style.position = "absolute";
			iframe.style.top = "0";
			iframe.style.left = "0";
			iframe.style.width = "100%";
			iframe.style.height = "100%";

			// Replace poster with iframe
			iframeContainer.appendChild(iframe);
			iframeContainer.style.display = "block";
			if (poster) poster.style.display = "none";
		});
	}

	/**
	 * Initialize uploaded video functionality
	 */
	initUploadedVideo(block) {
		const video = block.querySelector(".video-block__video");
		if (!video) return;

		// Add lazy loading
		if ("IntersectionObserver" in window) {
			const observer = new IntersectionObserver(
				entries => {
					entries.forEach(entry => {
						if (entry.isIntersecting) {
							video.load();
							observer.unobserve(video);
						}
					});
				},
				{ threshold: 0.25 }
			);

			observer.observe(video);
		}
	}

	/**
	 * Initialize URL management
	 */
	initURLManagement() {
		// Listen for browser back/forward
		window.addEventListener("popstate", () => {
			let tab = null;

			// Parse URL for Shopify collection tag format
			const currentPath = window.location.pathname;
			const collectionBasePath = `/collections/${this.settings.collectionHandle}`;

			if (currentPath.startsWith(collectionBasePath + "/")) {
				// Extract tag portion from URL
				const tagPortion = currentPath.substring(
					collectionBasePath.length + 1
				);

				if (tagPortion && tagPortion.length > 0) {
					// Split multiple tags by + separator
					const urlTags = tagPortion
						.split("+")
						.map(tag => tag.trim().toLowerCase());

					// Find matching tab based on URL tags
					tab = this.findTabByTags(urlTags);
				}
			}

			// Fallback: Check for legacy filter parameter (for backward compatibility)
			if (!tab) {
				const urlParams = new URLSearchParams(window.location.search);
				const filterTag = urlParams.get("filter.p.tag");
				if (filterTag) {
					const matchingTab = this.settings.tabs.find(tabConfig =>
						tabConfig.tags.some(
							tag => tag.toLowerCase().trim() === filterTag.toLowerCase()
						)
					);
					if (matchingTab) {
						tab = matchingTab.id;
					}
				}
			}

			// Default to first tab if no tab specified
			if (!tab && this.settings.tabs.length > 0) {
				tab = this.settings.tabs[0].id;
			}

			this.switchTab(tab);
		});
	}

	/**
	 * Update URL to use Shopify collection tag format: /collections/collection-name/[tagname]
	 */
	updateURL(tabType) {
		// Find the current tab and its tags
		const currentTab = this.settings.tabs.find(tab => tab.id === tabType);

		if (!currentTab || !currentTab.tags || currentTab.tags.length === 0) {
			// If no tags, redirect to base collection URL
			const baseCollectionURL = `/collections/${this.settings.collectionHandle}`;
			history.pushState({ tab: tabType }, "", baseCollectionURL);
			return;
		}

		// Get and clean the tags for this tab
		const cleanTags = currentTab.tags
			.map(tag => tag.trim().toLowerCase())
			.filter(tag => tag.length > 0);

		if (cleanTags.length === 0) {
			// If no valid tags, redirect to base collection URL
			const baseCollectionURL = `/collections/${this.settings.collectionHandle}`;
			history.pushState({ tab: tabType }, "", baseCollectionURL);
			return;
		}

		// Build the Shopify tag URL format
		let taggedURL;
		if (cleanTags.length === 1) {
			// Single tag: /collections/collection-name/tagname
			taggedURL = `/collections/${this.settings.collectionHandle}/${cleanTags[0]}`;
		} else {
			// Multiple tags: /collections/collection-name/tagname+tagname
			const tagString = cleanTags.join("+");
			taggedURL = `/collections/${this.settings.collectionHandle}/${tagString}`;
		}

		// Update URL without page reload
		history.pushState({ tab: tabType }, "", taggedURL);
	}

	/**
	 * Set initial tab from URL parameters or default
	 */
	setInitialTabFromURL() {
		if (!this.settings.tabs || this.settings.tabs.length === 0) return;

		let tab = null;

		// Check URL for tab parameter
		const urlParams = new URLSearchParams(window.location.search);
		const urlTab = urlParams.get("tab");

		if (urlTab) {
			// Check if the URL tab exists in our settings
			const tabExists = this.settings.tabs.some(
				tabConfig => tabConfig.id === urlTab
			);
			if (tabExists) {
				tab = urlTab;
			}
		}

		// If no URL tab, check for URL tags
		if (!tab) {
			const urlTags = this.getURLTags();
			if (urlTags.length > 0) {
				tab = this.findTabByTags(urlTags);
			}
		}

		// If still no tab, use default
		if (!tab) {
			if (this.settings.defaultTabId) {
				const defaultTabExists = this.settings.tabs.some(
					tabConfig => tabConfig.id === this.settings.defaultTabId.trim()
				);
				if (defaultTabExists) {
					tab = this.settings.defaultTabId.trim();
				}
			}

			// Fall back to first tab if default doesn't exist
			if (!tab && this.settings.tabs.length > 0) {
				tab = this.settings.tabs[0].id;
			}
		}

		// Apply initial state without triggering loading animations
		this.applyInitialTabState(tab);
	}

	/**
	 * Get URL tags from the current collection URL
	 */
	getURLTags() {
		const currentPath = window.location.pathname;
		const collectionBasePath = `/collections/${this.settings.collectionHandle}`;

		if (currentPath.startsWith(collectionBasePath + "/")) {
			// Extract tag portion from URL
			const tagPortion = currentPath.substring(
				collectionBasePath.length + 1
			);

			if (tagPortion && tagPortion.length > 0) {
				// Split multiple tags by + separator
				return tagPortion.split("+").map(tag => tag.trim().toLowerCase());
			}
		}

		return [];
	}

	/**
	 * Find tab that matches the given URL tags
	 */
	findTabByTags(urlTags) {
		// Find tab that has any of the URL tags
		for (const tabConfig of this.settings.tabs) {
			if (!tabConfig.tags || tabConfig.tags.length === 0) continue;

			const tabTags = tabConfig.tags.map(tag => tag.trim().toLowerCase());

			// Check if any URL tag matches any tab tag
			const hasMatch = urlTags.some(urlTag =>
				tabTags.some(tabTag => tabTag === urlTag)
			);

			if (hasMatch) {
				return tabConfig.id;
			}
		}

		return null;
	}

	/**
	 * Apply initial tab state from URL or default
	 */
	applyInitialTabState(tabType) {
		if (!this.settings.tabs || this.settings.tabs.length === 0) return;

		// Determine the correct tab based on URL without redirecting
		const currentPath = window.location.pathname;
		const baseCollectionURL = `/collections/${this.settings.collectionHandle}`;
		
		// If we're on a filtered URL, extract the intended tab from URL tags
		if (currentPath !== baseCollectionURL && currentPath.startsWith(baseCollectionURL + "/")) {
			const urlTags = this.getURLTags();
			const urlBasedTab = this.findTabByTags(urlTags);
			if (urlBasedTab) {
				tabType = urlBasedTab;
				console.log("Enhanced mode: Using tab from URL tags:", tabType);
			}
		}

		// Pre-load full collection data for instant filtering
		this.preloadCollectionData().then(() => {
			// Find the tab element and activate it
			this.tabs.forEach(tab => {
				if (tab.dataset.tab === tabType) {
					tab.classList.add("active");
					tab.setAttribute("aria-selected", "true");
				} else {
					tab.classList.remove("active");
					tab.setAttribute("aria-selected", "false");
				}
			});

			// Apply filtering using cached data for instant results
			this.filterProductsWithCache(tabType);

			// Belt-and-suspenders: remove .products-data if still present after async fetch
			// (should already be gone from the synchronous removal in initElements)
			const productsDataElAsync = document.querySelector('.products-data');
			if (productsDataElAsync) productsDataElAsync.remove();

			// Update URL to match the selected tab without page reload
			const currentTab = this.settings.tabs.find(tab => tab.id === tabType);
			if (currentTab && currentTab.tags && currentTab.tags.length > 0) {
				const expectedURL = this.buildFilterURL(tabType);
				if (window.history && window.history.replaceState) {
					window.history.replaceState(null, "", expectedURL);
				}
			}

			// Reinitialize Yotpo widgets after initial tab state is applied
			setTimeout(() => {
				this.reinitializeYotpo();
			}, 100);
		}).catch(error => {
			console.warn("Enhanced mode: Failed to preload data, falling back to standard filtering:", error);
			// Fallback to current behavior
			this.tabs.forEach(tab => {
				if (tab.dataset.tab === tabType) {
					tab.classList.add("active");
					tab.setAttribute("aria-selected", "true");
				} else {
					tab.classList.remove("active");
					tab.setAttribute("aria-selected", "false");
				}
			});

			this.filterProducts(tabType);

			const productsDataElFallback = document.querySelector('.products-data');
			if (productsDataElFallback) productsDataElFallback.remove();
		});
	}

	/**
	 * Initialize intersection observer for performance
	 */
	initIntersectionObserver() {
		if (!("IntersectionObserver" in window)) return;

		// Lazy load images in content blocks
		const images = this.section.querySelectorAll('img[loading="lazy"]');
		const imageObserver = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						const img = entry.target;
						if (img.dataset.src) {
							img.src = img.dataset.src;
							img.removeAttribute("data-src");
						}
						imageObserver.unobserve(img);
					}
				});
			},
			{ rootMargin: "50px" }
		);

		images.forEach(img => imageObserver.observe(img));
	}

	/**
	 * Initialize sorting integration
	 */
	initSortingIntegration() {
		// Listen for external sorting events from sort-by snippet
		window.addEventListener("alpine-content-updated", event => {
			if (event.detail.source === "sort-by") {
				this.handleExternalSorting(event.detail.sortValue);
			}
		});

		// Check for initial sort parameter in URL
		const urlParams = new URLSearchParams(window.location.search);
		const initialSort = urlParams.get("sort_by");
		if (initialSort) {
			this.currentSortBy = initialSort;
		}
	}

	/**
	 * Handle sorting from external sources (like sort-by snippet)
	 */
	handleExternalSorting(sortBy) {
		// Store current sort preference
		this.currentSortBy = sortBy;

		// Apply sorting to currently visible products
		this.applySortingToVisibleProducts(sortBy);

		// Update URL with sort parameter
		this.updateURLWithSort(sortBy);

		// Disable pagination after sorting
		this.disablePagination();
	}

	/**
	 * Apply sorting to currently visible products
	 */
	applySortingToVisibleProducts(sortBy) {
		if (!this.grid) return;

		// Get only products that are currently visible (not hidden by tab filtering)
		const visibleProducts = Array.from(
			this.grid.querySelectorAll(".product-grid-item")
		).filter(product => {
			const style = window.getComputedStyle(product);
			return style.display !== "none";
		});
		
		if (visibleProducts.length === 0) return;

		// Prevent multiple concurrent sorting operations
		if (this.isSorting) {
			return;
		}
		this.isSorting = true;

		try {
			// Sort products based on sort criteria (synchronous)
			const sortedProducts = this.sortProductsByCriteria(
				visibleProducts,
				sortBy
			);

			// Use reflowProducts method to properly handle blocks during sorting
			// This preserves blocks and their positioning unlike reorderProductsInDOM + GridInterleaver
			this.applySortingWithReflow(sortedProducts);
		} catch (error) {
			console.error("Error during sorting:", error);
		} finally {
			this.isSorting = false;
		}
	}

	/**
	 * Apply sorting with proper block handling using reflowProducts
	 */
	applySortingWithReflow(sortedProducts) {
		// Hide all products first
		const allProducts = this.grid.querySelectorAll(".product-grid-item");
		allProducts.forEach(product => {
			product.style.display = "none";
		});

		// Show sorted products in order with visual feedback
		sortedProducts.forEach((product, index) => {
			product.style.display = "";
			product.style.animationDelay = `${index * 0.05}s`;
			product.classList.add("fade-in");
		});

		// Use reflowProducts to properly position blocks relative to sorted products
		this.reflowProducts(sortedProducts);

		// Skip GridInterleaver for enhanced collections - our reflowProducts handles positioning
		console.log("🔍 DEBUG: Sorting complete - using reflowProducts positioning instead of GridInterleaver");

		// Update ARIA label for screen readers
		this.grid.setAttribute(
			"aria-label",
			`${sortedProducts.length} products displayed (sorted)`
		);
	}

	/**
	 * Sort products by specified criteria
	 */
	sortProductsByCriteria(products, sortBy) {
		const sortedProducts = [...products];

		switch (sortBy) {
			case "price-ascending":
				return sortedProducts.sort((a, b) => {
					const priceA = this.getProductPrice(a);
					const priceB = this.getProductPrice(b);
					return priceA - priceB;
				});

			case "price-descending":
				return sortedProducts.sort((a, b) => {
					const priceA = this.getProductPrice(a);
					const priceB = this.getProductPrice(b);
					return priceB - priceA;
				});

			case "title-ascending":
				return sortedProducts.sort((a, b) => {
					const titleA = this.getProductTitle(a).toLowerCase();
					const titleB = this.getProductTitle(b).toLowerCase();
					return titleA.localeCompare(titleB);
				});

			case "title-descending":
				return sortedProducts.sort((a, b) => {
					const titleA = this.getProductTitle(a).toLowerCase();
					const titleB = this.getProductTitle(b).toLowerCase();
					return titleB.localeCompare(titleA);
				});

			case "created-descending":
				return sortedProducts.sort((a, b) => {
					const dateA = this.getProductDate(a);
					const dateB = this.getProductDate(b);
					return new Date(dateB) - new Date(dateA);
				});

			case "created-ascending":
				return sortedProducts.sort((a, b) => {
					const dateA = this.getProductDate(a);
					const dateB = this.getProductDate(b);
					return new Date(dateA) - new Date(dateB);
				});

			case "best-selling":
				return sortedProducts.sort((a, b) => {
					const salesA = this.getProductSales(a);
					const salesB = this.getProductSales(b);
					return salesB - salesA;
				});

			case "manual":
			default:
				// Return to original order based on data-position
				return sortedProducts.sort((a, b) => {
					const posA = parseInt(a.dataset.position) || 0;
					const posB = parseInt(b.dataset.position) || 0;
					return posA - posB;
				});
		}
	}

	/**
	 * Helper methods to extract product data
	 */
	getProductPrice(productElement) {
		const priceElement = productElement.querySelector(
			".price, .product__price, .product-price"
		);
		if (!priceElement) return 0;

		const priceText = priceElement.textContent.replace(/[^0-9.]/g, "");
		return parseFloat(priceText) || 0;
	}

	getProductTitle(productElement) {
		const titleElement = productElement.querySelector(
			".product__title, .product-title, .product__name, .product-name"
		);
		return titleElement ? titleElement.textContent.trim() : "";
	}

	getProductDate(productElement) {
		return productElement.dataset.productDate || "";
	}

	getProductSales(productElement) {
		return parseInt(productElement.dataset.productSales) || 0;
	}

	/**
	 * Reorder products in DOM
	 */
	reorderProductsInDOM(sortedProducts) {
		const fragment = document.createDocumentFragment();

		sortedProducts.forEach(product => {
			fragment.appendChild(product);
		});

		// Remove only existing products, keep blocks for GridInterleaver to handle
		const existingProducts = this.grid.querySelectorAll(".product-grid-item");
		existingProducts.forEach(product => product.remove());

		// Add sorted products back (blocks will be repositioned by GridInterleaver)
		this.grid.appendChild(fragment);
	}

	/**
	 * Update URL with sort parameter
	 */
	updateURLWithSort(sortBy) {
		const url = new URL(window.location.href);

		if (sortBy === "manual") {
			url.searchParams.delete("sort_by");
		} else {
			url.searchParams.set("sort_by", sortBy);
		}

		history.replaceState(null, null, url.toString());
	}

	/**
	 * Reset filter state and show all products
	 */
	resetFilter() {
		const productGridItems = this.grid.querySelectorAll(".product-grid-item");
		const emptyStateElement = this.grid.querySelector(
			".collection-enhanced__empty-state"
		);

		// Show all products
		productGridItems.forEach(product => {
			product.style.display = "";
			product.classList.remove("fade-in");
		});

		// Reflow products to their original positions
		this.reflowProductsToOriginal();

		// Hide empty state if it exists
		if (emptyStateElement) {
			emptyStateElement.style.display = "none";
		}

		// Update ARIA label
		this.grid.setAttribute(
			"aria-label",
			`${productGridItems.length} products displayed`
		);
	}

	/**
	 * Reflow products back to their original positions
	 */
	reflowProductsToOriginal() {
		if (!this.grid) return;

		// Get all grid items and sort them by their original position
		const allGridItems = Array.from(this.grid.children);

		// Sort by original position (stored in data-position)
		allGridItems.sort((a, b) => {
			const posA = parseInt(a.dataset.position) || 0;
			const posB = parseInt(b.dataset.position) || 0;
			return posA - posB;
		});

		// Reorder the DOM elements
		allGridItems.forEach(item => {
			if (item.parentNode === this.grid) {
				this.grid.appendChild(item);
			}
		});
	}

	/**
	 * Uncheck all selected filters without triggering change events
	 */
	uncheckAllFilters() {
		// Set flag to indicate we're programmatically changing filters
		this.isProgrammaticallyChangingFilters = true;

		// Find all filter checkboxes and radio buttons within the entire document
		// This ensures we clear filters from sidebar and other filter locations
		// Note: We now include sort inputs to reset them on tab switches
		const filterInputs = document.querySelectorAll(
			'input[type="checkbox"], input[type="radio"]'
		);

		filterInputs.forEach(input => {
			if (input.checked) {
				// Store original event handlers
				const originalOnChange = input.onchange;
				const originalOnClick = input.onclick;

				// Temporarily remove event handlers
				input.onchange = null;
				input.onclick = null;

				// Prevent event bubbling by setting flags
				input._skipFilterEvent = true;
				input._programmaticChange = true;

				// Uncheck the input
				input.checked = false;

				// Trigger any necessary UI updates without filter logic
				if (input.closest("form")) {
					// Dispatch a custom event that other scripts can listen to for UI updates
					const customEvent = new CustomEvent("filter-reset", {
						detail: { input: input, programmatic: true },
					});
					input.dispatchEvent(customEvent);
				}

				// Restore event handlers after a delay
				setTimeout(() => {
					input.onchange = originalOnChange;
					input.onclick = originalOnClick;
					delete input._skipFilterEvent;
					delete input._programmaticChange;
				}, 50);
			}
		});

		// Also clear any filter form submissions or URL parameters
		const filterForms = document.querySelectorAll(
			"form[data-filter-form], .collection-sidebar form"
		);
		filterForms.forEach(form => {
			// Reset form without triggering submission
			const inputs = form.querySelectorAll(
				'input[type="checkbox"]:checked, input[type="radio"]:checked'
			);
			inputs.forEach(input => {
				// Now we uncheck ALL inputs including sort inputs
				input.checked = false;
			});
		});

		// Reset the programmatic flag
		setTimeout(() => {
			this.isProgrammaticallyChangingFilters = false;
		}, 100);
	}

	/**
	 * Enhanced product visibility protection
	 */
	ensureProductsVisible() {
		if (!this.grid) return;

		const products = this.grid.querySelectorAll(
			".product-grid-item, .product__thumbnail"
		);
		products.forEach(product => {
			// Only show products that aren't filtered out
			if (!product.style.display || product.style.display !== "none") {
				product.style.opacity = "1";
				product.style.visibility = "visible";
			}
		});
	}

	/**
	 * Inject CSS to override any pagination hiding styles
	 */
	injectProductVisibilityCSS() {
		const styleElement = document.createElement("style");
		styleElement.innerHTML = `
			.product-grid-item, .product__thumbnail {
				opacity: 1 !important;
				visibility: visible !important;
			}
			.product-grid-item[style*="display: none"], .product__thumbnail[style*="display: none"] {
				opacity: 0 !important;
				visibility: hidden !important;
			}
		`;
		document.head.appendChild(styleElement);
	}

	/**
	 * Set up product visibility protection observer
	 */
	setupProductVisibilityObserver() {
		if (!("MutationObserver" in window)) return;

		const observer = new MutationObserver(mutations => {
			mutations.forEach(mutation => {
				if (mutation.type === "childList") {
					mutation.addedNodes.forEach(node => {
						if (node.nodeType === Node.ELEMENT_NODE) {
							// Check if the added node is a product grid item or thumbnail
							const isProductGridItem =
								node.classList.contains("product-grid-item");
							const isProductThumbnail =
								node.classList.contains("product__thumbnail");

							if (isProductGridItem || isProductThumbnail) {
								// Ensure it's not hidden by any external means
								if (
									node.style.display === "none" ||
									node.style.opacity === "0"
								) {
									console.log(
										"Enhanced mode: Product visibility changed, forcing visible:",
										node
									);
									node.style.display = "";
									node.style.opacity = "1";
									node.style.visibility = "visible";
								}
							}
						}
					});
				}
			});
		});

		// Observe the entire document body for any changes to product grid items or thumbnails
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["style"],
		});
	}

	/**
	 * Override common hiding methods to prevent products from being hidden
	 */
	overrideHidingMethods() {
		// jQuery method overrides removed for testing

		// Note: Vanilla JavaScript doesn't have .hide() and .show() methods by default
		// These are typically jQuery methods, so we don't need to override them
		console.log(
			"Enhanced mode: Vanilla hiding methods not overridden (not needed)"
		);

		// Use a simpler approach - monitor and restore visibility instead of overriding CSS properties
		console.log(
			"Enhanced mode: Using visibility monitoring approach for product protection"
		);
	}

	/**
	 * Force all products to be visible and prevent hiding
	 */
	forceProductsVisible() {
		if (!this.grid) return;

		const products = this.grid.querySelectorAll(
			".product-grid-item, .product__thumbnail"
		);
		products.forEach(product => {
			// Only show products that aren't filtered out
			if (!product.style.display || product.style.display !== "none") {
				product.style.opacity = "1";
				product.style.visibility = "visible";
			}
		});
	}

	/**
	 * Add comprehensive debugging for product visibility
	 */
	addProductVisibilityDebugging() {
		if (!this.grid) return;

		const products = this.grid.querySelectorAll(
			".product-grid-item, .product__thumbnail"
		);

		products.forEach(product => {
			// Store original display and opacity
			product._originalDisplay = product.style.display;
			product._originalOpacity = product.style.opacity;
			product._originalVisibility = product.style.visibility;

			// Add a class to easily identify products that might be hidden
			product.classList.add("enhanced-product-debug");
		});

		console.log("Enhanced mode: Product visibility debugging initialized.");
	}

	// Logging methods removed for testing

	/**
	 * Set up periodic visibility checks
	 */
	setupPeriodicVisibilityChecks() {
		if (!this.grid) return;

		// Check every 5 seconds to ensure products are visible
		this.visibilityCheckInterval = setInterval(() => {
			this.ensureProductsVisible();
			console.log(
				"Enhanced mode: Periodic product visibility check completed."
			);
		}, 5000); // 5 seconds
	}

	/**
	 * Cleanup method to be called when component is destroyed
	 */
	destroy() {
		// Clear the cleanup interval
		if (this.cleanupInterval) {
			clearInterval(this.cleanupInterval);
		}

		// Clear the visibility check interval
		if (this.visibilityCheckInterval) {
			clearInterval(this.visibilityCheckInterval);
		}

		// Disconnect any observers
		if (this.paginationObserver) {
			this.paginationObserver.disconnect();
		}

		// Disconnect mutation observer
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
		}

		// Clear timeouts
		if (this.disablePaginationTimeout) {
			clearTimeout(this.disablePaginationTimeout);
		}
	}

	/**
	 * Pre-load full collection data for instant filtering
	 */
	async preloadCollectionData() {
		// Check if we have valid cached data
		const cachedData = this.getCachedData();
		if (cachedData) {
			console.log("Enhanced mode: Using cached collection data");
			this.cache.products = cachedData.products;
			this.cache.blocks = cachedData.blocks || [];
			return { products: cachedData.products, blocks: cachedData.blocks || [] };
		}

		console.log("Enhanced mode: Fetching fresh collection data");
		
		// Show loading state
		this.showPreloadingState();

		try {
			const baseCollectionURL = `/collections/${this.settings.collectionHandle}`;
			const response = await fetch(baseCollectionURL, {
				method: "GET",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
				},
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const html = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, "text/html");

			// Extract all products from the base collection
			const collectionGrid = doc.querySelector(".collection-enhanced__content");
			if (!collectionGrid) {
				throw new Error("Collection grid not found in response");
			}

			// Extract product data
			const productElements = collectionGrid.querySelectorAll(".product-grid-item");
			const productDataElements = doc.querySelectorAll(".product-data");
			
			// Extract blocks data  
			const blockElements = collectionGrid.querySelectorAll("[data-block-id]");

			// Build comprehensive dataset
			const allProducts = this.extractProductData(productElements, productDataElements);
			const allBlocks = this.extractBlockData(blockElements);
			
			// Cache the data
			this.setCachedData(allProducts, allBlocks);
			this.cache.products = allProducts;
			this.cache.blocks = allBlocks;

			this.hidePreloadingState();
			console.log(`Enhanced mode: Cached ${allProducts.length} products and ${allBlocks.length} blocks`);
			
			return { products: allProducts, blocks: allBlocks };
		} catch (error) {
			this.hidePreloadingState();
			console.error("Enhanced mode: Failed to preload collection data:", error);
			throw error;
		}
	}

	/**
	 * Extract block data from DOM elements
	 */
	extractBlockData(blockElements) {
		return Array.from(blockElements).map(element => {
			return {
				blockId: element.dataset.blockId,
				originalPositionAfter: element.dataset.originalPositionAfter,
				position: element.dataset.position,
				html: element.outerHTML,
				type: this.getBlockType(element)
			};
		});
	}

	/**
	 * Extract comprehensive product data from DOM elements
	 */
	extractProductData(productElements, productDataElements) {
		// Create map of product data from hidden data elements
		const productDataMap = new Map();
		productDataElements.forEach(dataElement => {
			const productId = dataElement.dataset.productId;
			if (productId) {
				productDataMap.set(productId, {
					productTags: dataElement.dataset.productTags || "",
					matchingTabs: dataElement.dataset.matchingTabs || "",
					position: dataElement.dataset.position || "",
				});
			}
		});

		// Combine visible elements with their data
		return Array.from(productElements).map(element => {
			const productId =
				element.dataset.productId ||
				element.querySelector("[data-product-id]")?.dataset.productId ||
				element.querySelector("form")?.dataset.productId;

			// Get data from element or data map
			let matchingTabs = element.dataset.matchingTabs || "";
			let productTags = element.dataset.productTags || "";

			if (!matchingTabs) {
				const productData = productDataMap.get(productId) || {};
				matchingTabs = productData.matchingTabs || "";
				productTags = productData.productTags || "";
			}

			return {
				element: element.cloneNode(true), // Clone for caching
				productId: productId,
				productTags: productTags,
				matchingTabs: matchingTabs,
				html: element.outerHTML,
			};
		});
	}

	/**
	 * Get cached collection data if still valid
	 */
	getCachedData() {
		try {
			const cached = sessionStorage.getItem(this.cacheKey);
			if (!cached) return null;

			const data = JSON.parse(cached);
			const now = Date.now();

			// Check if cache is still valid
			if (data.timestamp && (now - data.timestamp) < this.cacheExpiry && data.version === this.cache.version) {
				return data;
			}

			// Cache expired, remove it
			sessionStorage.removeItem(this.cacheKey);
			return null;
		} catch (error) {
			console.warn("Enhanced mode: Failed to parse cached data:", error);
			sessionStorage.removeItem(this.cacheKey);
			return null;
		}
	}

	/**
	 * Cache collection data in sessionStorage
	 */
	setCachedData(products, blocks) {
		try {
			const cacheData = {
				products: products.map(product => ({
					productId: product.productId,
					productTags: product.productTags,
					matchingTabs: product.matchingTabs,
					html: product.html,
				})),
				blocks: blocks.map(block => ({
					blockId: block.blockId,
					originalPositionAfter: block.originalPositionAfter,
					position: block.position,
					html: block.html,
					type: block.type
				})),
				timestamp: Date.now(),
				version: this.cache.version,
			};

			sessionStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
		} catch (error) {
			console.warn("Enhanced mode: Failed to cache data:", error);
		}
	}

	/**
	 * Filter products using cached data for instant results
	 */
	filterProductsWithCache(tabType) {
		if (!this.cache.products || !this.grid) {
			// Fallback to regular filtering
			this.filterProducts(tabType);
			return;
		}

		console.log("Enhanced mode: Fast filtering with cached data");

		// Find the tab configuration
		const currentTab = this.settings.tabs.find(tab => tab.id === tabType);
		const shouldShowAll = !currentTab || !currentTab.tags || currentTab.tags.length === 0;

		// Filter cached products
		let productsToShow = [];
		if (shouldShowAll) {
			productsToShow = this.cache.products;
		} else {
			productsToShow = this.cache.products.filter(product => {
				const matchingTabs = product.matchingTabs || "";
				return matchingTabs
					.split(",")
					.map(tab => tab.trim())
					.includes(tabType);
			});
		}

		// Clear current grid content
		this.grid.innerHTML = "";

		// Add filtered products and blocks with smooth transitions
		this.renderContentWithBlocks(productsToShow, this.cache.blocks || []);

		// Handle empty state
		if (productsToShow.length === 0) {
			this.handleEmptyFilterState(currentTab);
			return;
		}

		// Match row heights after rendering
		if (this.matchRowHeights) {
			setTimeout(() => this.matchRowHeights(), 150);
		}

		// Update ARIA label
		this.grid.setAttribute(
			"aria-label",
			`${productsToShow.length} products displayed`
		);

		// Reinitialize components
		setTimeout(() => {
			this.initProductHoverImages();
			this.initVideoBlocks();
		}, 100);
	}

	/**
	 * Render products and blocks with proper positioning and smooth transitions
	 */
	renderContentWithBlocks(products, blocks) {
		const fragment = document.createDocumentFragment();

		// Create new positioning array similar to reflowProducts
		const newOrder = [];
		
		// Count blocks with position_after: 0 to determine product offset
		const zeroPositionBlocks = blocks.filter(block => parseInt(block.originalPositionAfter) === 0);
		const zeroBlockCount = zeroPositionBlocks.length;
		
		console.log(`Enhanced mode: Found ${zeroBlockCount} blocks with position_after: 0`);
		
		// First, add blocks with position_after: 0 (should appear at the very beginning)
		zeroPositionBlocks.forEach((block, index) => {
			const newBlockPosition = index + 1;
			newOrder.push({
				html: block.html,
				position: newBlockPosition,
				type: "block",
				blockId: block.blockId
			});
		});

		// Then add products with their sequential positions, offset by zero-position blocks
		products.forEach((product, index) => {
			const newPosition = index + 1 + zeroBlockCount;
			newOrder.push({
				html: product.html,
				position: newPosition,
				type: "product",
				productId: product.productId
			});

			// Check if any blocks should be inserted after this product
			blocks.forEach(block => {
				const blockPositionAfter = parseInt(block.originalPositionAfter);
				if (blockPositionAfter === newPosition) {
					const newBlockPosition = newPosition + 1;
					newOrder.push({
						html: block.html,
						position: newBlockPosition,
						type: "block",
						blockId: block.blockId
					});
				}
			});
		});

		// Handle blocks that are positioned beyond the last visible product
		blocks.forEach(block => {
			const blockPositionAfter = parseInt(block.originalPositionAfter);
			const alreadyPositioned = newOrder.some(item => item.type === "block" && item.blockId === block.blockId);
			
			if (!alreadyPositioned && blockPositionAfter > products.length) {
				const endPosition = products.length + 1 + zeroBlockCount + 
					(newOrder.filter(item => item.type === "block" && item.position > products.length + zeroBlockCount).length);
				
				newOrder.push({
					html: block.html,
					position: endPosition,
					type: "block",
					blockId: block.blockId
				});
			}
		});

		// Sort by position to maintain proper order
		newOrder.sort((a, b) => a.position - b.position);

		console.log(`Enhanced mode: Rendering ${newOrder.length} items (${products.length} products, ${blocks.length} blocks)`);

		// Create elements from HTML and add smooth transitions
		newOrder.forEach((item, index) => {
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = item.html;
			const element = tempDiv.firstElementChild;

			// Add transition classes
			element.style.opacity = "0";
			element.style.transform = "translateY(20px)";
			element.style.transition = "opacity 0.3s ease, transform 0.3s ease";
			element.style.transitionDelay = `${index * 0.05}s`;

			fragment.appendChild(element);

			// Trigger animation after DOM insertion
			setTimeout(() => {
				element.style.opacity = "1";
				element.style.transform = "translateY(0)";
			}, 50);
		});

		this.grid.appendChild(fragment);

		// Update allProducts reference for compatibility  
		this.allProducts = products.map((product, index) => ({
			element: this.grid.querySelector(`[data-product-id="${product.productId}"]`),
			productId: product.productId,
			productTags: product.productTags,
			matchingTabs: product.matchingTabs,
		}));
	}

	/**
	 * Render products with smooth transitions
	 */
	renderProductsWithTransition(products) {
		const fragment = document.createDocumentFragment();

		products.forEach((product, index) => {
			// Create element from cached HTML
			const tempDiv = document.createElement('div');
			tempDiv.innerHTML = product.html;
			const productElement = tempDiv.firstElementChild;

			// Add transition classes
			productElement.style.opacity = "0";
			productElement.style.transform = "translateY(20px)";
			productElement.style.transition = "opacity 0.3s ease, transform 0.3s ease";
			productElement.style.transitionDelay = `${index * 0.05}s`;

			fragment.appendChild(productElement);

			// Trigger animation after DOM insertion
			setTimeout(() => {
				productElement.style.opacity = "1";
				productElement.style.transform = "translateY(0)";
			}, 50);
		});

		this.grid.appendChild(fragment);

		// Update allProducts reference for compatibility
		this.allProducts = products.map(product => ({
			element: this.grid.children[products.indexOf(product)],
			productId: product.productId,
			productTags: product.productTags,
			matchingTabs: product.matchingTabs,
		}));
	}

	/**
	 * Show preloading state with smooth loading indicator
	 */
	showPreloadingState() {
		if (!this.grid) return;

		// Create loading overlay if it doesn't exist
		if (!this.loadingOverlay) {
			this.loadingOverlay = document.createElement('div');
			this.loadingOverlay.className = 'enhanced-loading-overlay';
			this.loadingOverlay.innerHTML = `
				<div class="enhanced-loading-spinner">
					<div class="spinner-ring"></div>
					<p>Loading collection data...</p>
				</div>
			`;

			// Add CSS styles
			const style = document.createElement('style');
			style.textContent = `
				.enhanced-loading-overlay {
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					background: rgba(255, 255, 255, 0.9);
					display: flex;
					align-items: center;
					justify-content: center;
					z-index: 1000;
					transition: opacity 0.3s ease;
				}
				.enhanced-loading-spinner {
					text-align: center;
					color: #666;
				}
				.spinner-ring {
					width: 40px;
					height: 40px;
					margin: 0 auto 10px;
					border: 3px solid #f3f3f3;
					border-top: 3px solid #333;
					border-radius: 50%;
					animation: spin 1s linear infinite;
				}
				@keyframes spin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
			`;
			document.head.appendChild(style);
		}

		// Position overlay relative to grid
		this.grid.style.position = 'relative';
		this.grid.appendChild(this.loadingOverlay);
		
		// Fade in effect
		setTimeout(() => {
			this.loadingOverlay.style.opacity = '1';
		}, 10);
	}

	/**
	 * Hide preloading state with smooth transition
	 */
	hidePreloadingState() {
		if (this.loadingOverlay && this.loadingOverlay.parentNode) {
			// Fade out effect
			this.loadingOverlay.style.opacity = '0';
			setTimeout(() => {
				if (this.loadingOverlay.parentNode) {
					this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
				}
			}, 300);
		}
	}

	/**
	 * Ensure blocks are visible and properly positioned
	 */
	ensureBlocksAreVisible() {
		if (!this.cache.blocks || !this.grid) return;

		const existingBlocks = this.grid.querySelectorAll('[data-block-id]');
		const existingBlockIds = Array.from(existingBlocks).map(block => block.dataset.blockId);

		// Check if any cached blocks are missing from the DOM
		this.cache.blocks.forEach(cachedBlock => {
			if (!existingBlockIds.includes(cachedBlock.blockId)) {
				console.log(`Enhanced mode: Re-adding missing block ${cachedBlock.blockId}`);
				
				// Create the missing block element
				const tempDiv = document.createElement('div');
				tempDiv.innerHTML = cachedBlock.html;
				const blockElement = tempDiv.firstElementChild;
				
				// Add to grid - positioning will be handled by next reflow
				this.grid.appendChild(blockElement);
			}
		});

		// Trigger a reflow to ensure proper positioning
		const visibleProducts = Array.from(this.grid.querySelectorAll(".product-grid-item:not([style*='display: none'])"));
		if (visibleProducts.length > 0) {
			this.reflowProducts(visibleProducts);
		}
	}

	/**
	 * Reinitialize Yotpo widgets after content changes
	 */
	reinitializeYotpo() {
		try {
			// Check if Yotpo is available on the page
			if (typeof window.yotpo !== 'undefined' && typeof window.Yotpo !== 'undefined') {
				const api = new window.Yotpo.API(window.yotpo);
				api.refreshWidgets();
			}
		} catch (error) {
			console.warn('Yotpo reinitialization failed:', error);
		}
	}

	// Filter change monitoring methods removed for testing
}

/**
 * Accessibility enhancements
 */
class CollectionAccessibility {
	constructor() {
		this.initKeyboardNavigation();
		this.initAriaLabels();
		this.initFocusManagement();
	}

	initKeyboardNavigation() {
		// Tab navigation with arrow keys
		const tabs = document.querySelectorAll(".collection-tab");

		tabs.forEach((tab, index) => {
			tab.addEventListener("keydown", e => {
				let nextIndex;

				switch (e.key) {
					case "ArrowLeft":
						e.preventDefault();
						nextIndex = index > 0 ? index - 1 : tabs.length - 1;
						tabs[nextIndex].focus();
						break;
					case "ArrowRight":
						e.preventDefault();
						nextIndex = index < tabs.length - 1 ? index + 1 : 0;
						tabs[nextIndex].focus();
						break;
					case "Home":
						e.preventDefault();
						tabs[0].focus();
						break;
					case "End":
						e.preventDefault();
						tabs[tabs.length - 1].focus();
						break;
				}
			});
		});
	}

	initAriaLabels() {
		// Add dynamic aria labels for products count
		const updateProductsCount = () => {
			const grid = document.querySelector(".collection-enhanced__content");
			if (!grid) return;

			const visibleProducts = grid.querySelectorAll(
				".product-grid-item:not([style*='display: none']) .product__thumbnail"
			).length;

			grid.setAttribute(
				"aria-label",
				`${visibleProducts} products displayed`
			);
		};

		// Update on tab change - watch for style changes on product items
		const observer = new MutationObserver(updateProductsCount);
		const grid = document.querySelector(".collection-enhanced__content");

		if (grid) {
			observer.observe(grid, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ["style"],
			});
			updateProductsCount(); // Initial count
		}
	}

	initFocusManagement() {
		// Announce content changes to screen readers
		const announceFilterChange = tabName => {
			const announcement = document.createElement("div");
			announcement.setAttribute("aria-live", "polite");
			announcement.setAttribute("aria-atomic", "true");
			announcement.className = "sr-only";
			announcement.textContent = `Showing ${tabName} products`;

			document.body.appendChild(announcement);

			setTimeout(() => {
				document.body.removeChild(announcement);
			}, 1000);
		};

		// Listen for tab changes
		document.addEventListener("click", e => {
			if (e.target.classList.contains("collection-tab")) {
				const tabName = e.target.textContent.trim();
				setTimeout(() => announceFilterChange(tabName), 500);
			}
		});
	}
}

/**
 * Performance monitoring
 */
class CollectionPerformance {
	constructor() {
		this.initPerformanceTracking();
	}

	initPerformanceTracking() {
		// Track Core Web Vitals
		if ("PerformanceObserver" in window) {
			this.trackLCP();
			this.trackCLS();
			this.trackFID();
		}
	}

	trackLCP() {
		const observer = new PerformanceObserver(list => {
			const entries = list.getEntries();
			const lastEntry = entries[entries.length - 1];
			console.log("LCP:", lastEntry.startTime);
		});
		observer.observe({ entryTypes: ["largest-contentful-paint"] });
	}

	trackCLS() {
		let clsValue = 0;
		const observer = new PerformanceObserver(list => {
			for (const entry of list.getEntries()) {
				if (!entry.hadRecentInput) {
					clsValue += entry.value;
				}
			}
			console.log("CLS:", clsValue);
		});
		observer.observe({ entryTypes: ["layout-shift"] });
	}

	trackFID() {
		const observer = new PerformanceObserver(list => {
			for (const entry of list.getEntries()) {
				console.log("FID:", entry.processingStart - entry.startTime);
			}
		});
		observer.observe({ entryTypes: ["first-input"] });
	}
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
	const section = document.querySelector(".collection-enhanced");
	if (section) {
		new CollectionEnhanced();
		new CollectionAccessibility();

		// Only track performance in development
		if (
			window.location.hostname === "localhost" ||
			window.location.hostname.includes("dev")
		) {
			new CollectionPerformance();
		}
	}
});

// Handle theme editor updates
document.addEventListener("shopify:section:load", e => {
	if (e.detail.sectionId.includes("collection-enhanced")) {
		new CollectionEnhanced();
	}
});

document.addEventListener("shopify:section:reorder", e => {
	if (e.detail.sectionId.includes("collection-enhanced")) {
		new CollectionEnhanced();
	}
});

// Export for potential external use
window.CollectionEnhanced = CollectionEnhanced;
